// React hooks for the admin Topics & Tags page. Hits /admin/articles/tags
// when VITE_ARTICLES_API is on; otherwise hydrates from the in-memory
// TOPICS fixture.

import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { articlesApiEnabled } from "./articlesApi";
import { TOPICS } from "./admin-mock";
import type { KindKey } from "@/components/admin-ui";

export interface TagWire {
  tag: string;
  label: string;
  kind: KindKey;
  count: number;
  leads: number;
  created_at: string;
  archived_at?: string | null;
}

export function useTags(includeArchived = false): {
  tags: TagWire[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [tags, setTags] = useState<TagWire[]>(() =>
    articlesApiEnabled() ? [] : mockTags(includeArchived),
  );
  const [loading, setLoading] = useState(articlesApiEnabled());
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!articlesApiEnabled()) {
      setTags(mockTags(includeArchived));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<{ tags: TagWire[] }>(
        `/admin/articles/tags${includeArchived ? "?include_archived=1" : ""}`,
      )
      .then((resp) => {
        if (cancelled) return;
        setTags(resp.tags);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick, includeArchived]);

  return { tags, loading, error, refresh: () => setTick((t) => t + 1) };
}

// mockTags converts TOPICS into wire shape so the page renders the
// same way in offline dev.
function mockTags(includeArchived: boolean): TagWire[] {
  // `includeArchived` is honoured for API parity but the mock has no
  // archived rows; the parameter just suppresses an unused-arg lint.
  void includeArchived;
  return TOPICS.map((t) => ({
    tag: t.tag.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
    label: t.tag,
    kind: t.kind,
    count: t.count,
    leads: t.leads,
    created_at: new Date(Date.now() - 30 * 86400_000).toISOString(),
  }));
}

export function useTagActions() {
  return useMemo(
    () => ({
      enabled: articlesApiEnabled(),
      async create(label: string, kind: KindKey): Promise<void> {
        if (!articlesApiEnabled()) return;
        await api.post(`/admin/articles/tags`, { label, kind });
      },
      async rename(tag: string, label: string): Promise<void> {
        if (!articlesApiEnabled()) return;
        await api.patch(`/admin/articles/tags/${encodeURIComponent(tag)}`, { label });
      },
      async archive(tag: string, archived = true): Promise<void> {
        if (!articlesApiEnabled()) return;
        await api.patch(`/admin/articles/tags/${encodeURIComponent(tag)}`, { archived });
      },
      async merge(from: string, into: string): Promise<void> {
        if (!articlesApiEnabled()) return;
        await api.post(`/admin/articles/tags/${encodeURIComponent(from)}/merge`, { into });
      },
    }),
    [],
  );
}
